const MAX_IMAGE_DIMENSION = 1600;
const IMAGE_UPLOAD_QUALITY = 0.82;

// Files larger than this are rejected before any decoding attempt.
// Loading a 30MB+ raw image into a canvas can exhaust memory on mobile devices,
// causing long hangs or browser crashes. Moderately oversized files (5–25MB)
// are still accepted because compression typically brings them under the 5MB limit.
const MAX_RAW_FILE_BYTES = 25 * 1024 * 1024; // 25MB

// Detect canvas WebP encoding support once per page load.
// WebP is preferred: better compression than JPEG and preserves alpha transparency.
// Falls back to JPEG on browsers that don't support WebP canvas encoding (Safari < 14).
const getOutputMime = (() => {
  let cached = null;
  return () => {
    if (cached === null) {
      const c = document.createElement("canvas");
      c.width = 1;
      c.height = 1;
      cached = c.toDataURL("image/webp").startsWith("data:image/webp")
        ? "image/webp"
        : "image/jpeg";
    }
    return cached;
  };
})();

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () =>
      reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });

const loadImageElement = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to decode image: ${file.name}`));
    };
    img.src = objectUrl;
  });

/**
 * Compresses an image file for upload by resizing to at most MAX_IMAGE_DIMENSION
 * on the longest side and re-encoding as WebP (or JPEG as fallback).
 *
 * If compression produces a larger payload than the original, the original
 * data URL is returned instead.
 *
 * If the browser cannot decode the image (e.g. memory pressure on large gallery
 * photos, or an unusual format), falls back to sending the raw file so the
 * server can attempt to handle it with sharp.
 *
 * @param {File} file
 * @returns {Promise<string>} data URL of the compressed (or original) image
 */
export const compressImageForUpload = async (file) => {
  if (!file.type || !file.type.startsWith("image/")) {
    return readFileAsDataUrl(file);
  }

  if (file.size > MAX_RAW_FILE_BYTES) {
    throw new Error(
      `${file.name} is too large to process (${(file.size / (1024 * 1024)).toFixed(0)}MB). Please use a photo under 25MB.`,
    );
  }

  let img;
  try {
    img = await loadImageElement(file);
  } catch (decodeErr) {
    // Browser could not decode the image (memory pressure, unusual format, etc.).
    // Send the raw file and let the server validate it. This surfaces a clearer
    // server-side error (e.g. "Unsupported image type") rather than a generic
    // client-side decode failure.
    console.warn(
      `[imageCompression] Could not decode ${file.name} (type=${file.type}, size=${file.size}B) in browser — sending raw file: ${decodeErr.message}`,
    );
    return readFileAsDataUrl(file);
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return readFileAsDataUrl(file);
  }

  const originalWidth = img.naturalWidth || img.width;
  const originalHeight = img.naturalHeight || img.height;

  if (!originalWidth || !originalHeight) {
    return readFileAsDataUrl(file);
  }

  const maxSide = Math.max(originalWidth, originalHeight);
  const scale =
    maxSide > MAX_IMAGE_DIMENSION ? MAX_IMAGE_DIMENSION / maxSide : 1;
  const targetWidth = Math.max(1, Math.round(originalWidth * scale));
  const targetHeight = Math.max(1, Math.round(originalHeight * scale));

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const outputMime = getOutputMime();
  // JPEG has no alpha channel; prefill white so transparent areas don't render black
  if (outputMime === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  const compressedDataUrl = canvas.toDataURL(outputMime, IMAGE_UPLOAD_QUALITY);

  canvas.width = 1;
  canvas.height = 1;

  if (!compressedDataUrl || compressedDataUrl.length === 0) {
    return readFileAsDataUrl(file);
  }

  // If compression makes payload larger, keep original encoding.
  // Compare decoded bytes of the compressed output against the original file size.
  const compressedBase64 = compressedDataUrl.slice(compressedDataUrl.indexOf(",") + 1);
  const compressedPadding = (compressedBase64.match(/={1,2}$/) || [""])[0].length;
  const compressedBytes = Math.floor((compressedBase64.length * 3) / 4) - compressedPadding;
  if (compressedBytes >= file.size) {
    return readFileAsDataUrl(file);
  }

  return compressedDataUrl;
};
